import { FC } from 'react';

import Button from '../../../common/button';
import { MoveCircle } from '../../../icons/move-circle';
import { MoveCross } from '../../../icons/move-cross';
import { MoveEightHorizontal } from '../../../icons/move-eight-horizontal';
import { MoveEightVertical } from '../../../icons/move-eight-vertical';
import { MoveLeft } from '../../../icons/move-left';
import { MoveTop } from '../../../icons/move-top';
import { MoveTopLeft } from '../../../icons/move-top-left';
import { MoveTopRight } from '../../../icons/move-top-right';

import { Trajectory } from '../../../../hooks/useDpdgState';
import { IconProps } from '../../../../types/icon.types';

interface IDpdgControlPanelProps {
  isMoving: boolean;
  onToggleMoving: () => void;
  trajectory: Trajectory;
  changeTrajectory?: (trajectory: Trajectory) => void;
  className?: string;
  loadingProgress?: number;
}

const trajectoryOptions: Array<{ value: Trajectory; label: string; Icon: FC<IconProps> }> = [
  { value: 'horizontal', label: 'горизонталь', Icon: MoveLeft },
  { value: 'vertical', label: 'вертикаль', Icon: MoveTop },
  { value: 'diagonal-br-tl', label: 'диагональ снизу справа вверх влево', Icon: MoveTopLeft },
  { value: 'diagonal-bl-tr', label: 'диагональ снизу слева вверх вправо', Icon: MoveTopRight },
  { value: 'circle', label: 'круг', Icon: MoveCircle },
  { value: 'eight-vertical', label: 'вертикальная восьмёрка', Icon: MoveEightVertical },
  { value: 'eight-horizontal', label: 'горизонтальная восьмёрка', Icon: MoveEightHorizontal },
  { value: 'cross', label: 'крест', Icon: MoveCross },
];

export const DpdgControlPanel: FC<IDpdgControlPanelProps> = ({
  isMoving,
  onToggleMoving,
  trajectory,
  changeTrajectory,
  className,
  loadingProgress,
}) => {
  const canChangeTrajectory = typeof changeTrajectory === 'function';
  const shouldShowProgress = typeof loadingProgress === 'number' && loadingProgress >= 0 && loadingProgress < 1;
  const progressWidth = shouldShowProgress ? Math.max(0, Math.min(loadingProgress, 1)) * 100 : 0;

  return (
    <div
      className={`relative flex w-full flex-nowrap items-center justify-center gap-3 overflow-x-auto bg-black/60 px-4 py-3 backdrop-blur-[2px] ${className ?? ''}`}
    >
      {shouldShowProgress && (
        <div className="absolute inset-x-0 top-0 h-[3px] overflow-hidden rounded-t-[8px] bg-white/20">
          <div
            className="h-full bg-goldBase transition-[width] duration-200 ease-out"
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      )}
      <Button
        type="grey"
        className="flex !w-auto min-w-[180px] items-center justify-center gap-2 !rounded-full !border-white/30 !bg-white/10 !px-6 !py-2 text-white transition-colors hover:!bg-white/20"
        onClick={onToggleMoving}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
          {isMoving ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="8" height="8" rx="1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 8L5 3V13L13 8Z" />
            </svg>
          )}
        </span>
        {isMoving ? 'Стоп' : 'Старт'}
      </Button>

      <div className="flex items-center gap-2">
        {trajectoryOptions.map(({ value, label, Icon }) => {
          const isActive = trajectory === value;

          return (
            <button
              key={value}
              type="button"
              onClick={() => canChangeTrajectory && changeTrajectory?.(value)}
              disabled={!canChangeTrajectory}
              className={`flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 transition-colors ${
                isActive ? 'border-white/60 bg-white/25 shadow-[0_0_12px_rgba(255,255,255,0.25)]' : 'hover:bg-white/20'
              } ${!canChangeTrajectory ? 'cursor-not-allowed opacity-50 hover:bg-white/10' : ''}`}
              aria-label={`Изменить траекторию: ${label}`}
            >
              <Icon />
            </button>
          );
        })}
      </div>
    </div>
  );
};
